import React from 'react';
import { motion } from 'framer-motion';
import type { Player, PlayerId, CallState } from '../../types/bountifulSea';
import { Die } from './DiceDisplay';

// ═══════════════════════════════════════════════════════════════
// SCOUNDREL CALL COMPONENTS
// ═══════════════════════════════════════════════════════════════

interface ScoundrelCalloutProps {
  callablePlayers: Player[];
  targetPlayer: Player;
  onCall: (callerId: PlayerId) => void;
  onPass: (playerId: PlayerId) => void;
}

export const ScoundrelCallout: React.FC<ScoundrelCalloutProps> = ({
  callablePlayers,
  targetPlayer,
  onCall,
  onPass,
}) => {
  // Filter out AI players (they handle their own calls)
  const humanCallers = callablePlayers.filter((p) => !p.isAI);

  if (humanCallers.length === 0) {
    return (
      <div className="bs-call-section">
        <div className="bs-call-title">Awaiting Calls...</div>
        <p className="bs-call-subtitle">
          Other players are deciding whether to call Scoundrel...
        </p>
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
          style={{ fontSize: '2rem' }}
        >
          ⏳
        </motion.div>
      </div>
    );
  }

  const voyage = targetPlayer.currentVoyage;
  const tideDie = voyage?.tideDieValue ?? 0;
  const scoundrelDiceCount = voyage?.scoundrelDiceValues.length ?? 0;

  return (
    <div className="bs-call-section">
      <motion.div
        className="bs-call-title"
        animate={{ scale: [1, 1.05, 1] }}
        transition={{ duration: 1.5, repeat: Infinity }}
      >
        Call Scoundrel?
      </motion.div>
      <p className="bs-call-subtitle">
        {targetPlayer.name} declared <strong style={{ color: 'var(--bs-gold)' }}>{voyage?.declaredTotal}</strong> coins.
        Do you believe them?
      </p>

      {/* Show visible dice info */}
      <div className="bs-call-dice-info">
        <div className="bs-call-dice-row">
          <span className="bs-call-dice-label">Tide Die:</span>
          <Die value={tideDie} isTide />
        </div>
        <div className="bs-call-dice-row">
          <span className="bs-call-dice-label">Hidden Dice:</span>
          {Array.from({ length: scoundrelDiceCount }).map((_, i) => (
            <div key={i} className="bs-die bs-die-hidden">?</div>
          ))}
        </div>
      </div>

      {humanCallers.map((player) => (
        <div key={player.id} className="bs-call-buttons">
          <motion.button
            className="bs-btn bs-btn-scoundrel"
            onClick={() => onCall(player.id)}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            {player.name}: SCOUNDREL!
          </motion.button>
          <motion.button
            className="bs-btn bs-btn-secondary"
            onClick={() => onPass(player.id)}
            whileHover={{ scale: 1.05 }}
          >
            Pass
          </motion.button>
        </div>
      ))}
    </div>
  );
};

// Call Resolution Display
interface CallResolutionProps {
  call: CallState;
  targetPlayer: Player;
  callerPlayer: Player;
  voyage: { tideDieValue: number; scoundrelDiceValues: number[]; actualTotal: number; declaredTotal: number };
  onContinue: () => void;
}

export const CallResolution: React.FC<CallResolutionProps> = ({
  call,
  targetPlayer,
  callerPlayer,
  voyage,
  onContinue,
}) => {
  const wasLying = call.wasLying;
  // Calculate this roll's actual total from the dice values shown
  const thisRollTotal = voyage.tideDieValue + voyage.scoundrelDiceValues.reduce((a, b) => a + b, 0);

  return (
    <div className="bs-resolution">
      <motion.div
        className={`bs-resolution-verdict ${wasLying ? 'bs-resolution-caught' : 'bs-resolution-truth'}`}
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ type: 'spring', duration: 0.5 }}
      >
        {wasLying ? 'CAUGHT!' : 'TRUTH!'}
      </motion.div>

      <div className="bs-resolution-details">
        <p>
          {targetPlayer.name} declared <strong>{voyage.declaredTotal}</strong> coins
        </p>

        <div className="bs-resolution-dice">
          <span>Tide:</span>
          <Die value={voyage.tideDieValue} isTide />
          {voyage.scoundrelDiceValues.map((val, i) => (
            <React.Fragment key={i}>
              <span>+</span>
              <Die value={val} />
            </React.Fragment>
          ))}
          <span>=</span>
          <strong style={{ fontSize: '1.5rem', color: 'var(--bs-gold)' }}>
            {thisRollTotal}
          </strong>
        </div>

        <p style={{ marginTop: '1rem' }}>
          {wasLying ? (
            <>
              <span style={{ color: callerPlayer.color }}>{callerPlayer.name}</span> was right!{' '}
              <span style={{ color: targetPlayer.color }}>{targetPlayer.name}</span> loses a Scoundrel Die.
            </>
          ) : (
            <>
              <span style={{ color: targetPlayer.color }}>{targetPlayer.name}</span> was truthful!{' '}
              <span style={{ color: callerPlayer.color }}>{callerPlayer.name}</span> loses a Scoundrel Die.
            </>
          )}
        </p>
      </div>

      <motion.button
        className="bs-btn bs-btn-primary"
        onClick={onContinue}
        whileHover={{ scale: 1.05 }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1 }}
      >
        Continue
      </motion.button>
    </div>
  );
};

export default ScoundrelCallout;
